/**
 * R10-14 — pacotes do instrutor no lado da SPA.
 *
 * Quem decide e o backend (`InstructorAccess::Resolver`, devolvido em `/auth/me` como
 * `instructor_access`). Este modulo so LE esse snapshot para esconder do menu de /instructor
 * o que os pacotes nao liberam e trocar a rota por um aviso. A API recusa do mesmo jeito
 * (403 `feature_not_in_package`).
 *
 * As chaves espelham `InstructorAccess::FeatureCatalog` (medhunter-api). Modulo puro.
 */
import type { InstructorAccess, InstructorFeatureKey } from '@/types/api'

export const ZOOM_FEATURE: InstructorFeatureKey = 'aulas_ao_vivo_zoom'

/** Texto do aviso de rota bloqueada. */
export const NOT_IN_PACKAGES_MESSAGE = 'Este recurso não está incluído nos seus pacotes.'

/**
 * Prefixo de rota -> feature. A primeira que casar vence (especificas antes). Rota fora da
 * tabela (Dashboard, Meu Perfil, Assistente IA) nao exige feature.
 */
export const INSTRUCTOR_ROUTE_FEATURES: ReadonlyArray<readonly [RegExp, InstructorFeatureKey]> = [
  [/^\/instructor\/students\/[^/]+\/meetings\/[^/]+\/transcript(\/|$)/, 'transcritor'],
  [/^\/instructor\/students(\/|$)/, 'alunos'],
  [/^\/instructor\/central-do-aprovado(\/|$)/, 'central_do_aprovado'],
  [/^\/instructor\/relatorio-do-ciclo(\/|$)/, 'relatorio_ciclo'],
  [/^\/instructor\/espelho(\/|$)/, 'meu_espelho'],
  [/^\/instructor\/administrativa(\/|$)/, 'administrativa'],
  [/^\/instructor\/transcritor(\/|$)/, 'transcritor'],
  [/^\/instructor\/aulas-ao-vivo(\/|$)/, ZOOM_FEATURE],
]

interface InstructorAccessHolder {
  role?: string
  instructor_access?: InstructorAccess | null
}

/** A feature que a rota exige, ou `null`. */
export function instructorRouteFeature(pathname: string): InstructorFeatureKey | null {
  const hit = INSTRUCTOR_ROUTE_FEATURES.find(([pattern]) => pattern.test(pathname))
  return hit ? hit[1] : null
}

/**
 * Admin tem tudo. Sem usuario ainda carregado, sem o campo (API anterior ao R10-14) ou sem
 * nenhum pacote cadastrado (`configured: false`), nada e bloqueado: o layout ja exige login e
 * o servidor recusa de qualquer jeito.
 */
export function hasInstructorFeature(user: InstructorAccessHolder | null | undefined, feature: InstructorFeatureKey): boolean {
  if (!user) return true
  if (user.role === 'admin') return true
  const access = user.instructor_access
  if (!access) return true
  if (access.full_access || !access.configured) return true
  return access.features.includes(feature)
}

/** A feature que falta para abrir a rota, ou `null` se esta liberada. */
export function missingInstructorRouteFeature(
  user: InstructorAccessHolder | null | undefined,
  pathname: string,
): InstructorFeatureKey | null {
  const feature = instructorRouteFeature(pathname)
  if (!feature) return null
  return hasInstructorFeature(user, feature) ? null : feature
}
